import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const ReturnsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="returns-page">
      <Header />

      <main className="pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1C] mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Back
          </button>

          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-12">
            Returns & Refunds Policy
          </h1>

          <div className="space-y-10">
            {/* Return Window */}
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Return Window</h2>
              <p className="text-[#555] leading-relaxed">
                Customers may return items within <span className="font-semibold text-[#1A1A1C]">14 days</span> of delivery for a full refund.
              </p>
            </section>

            <hr className="border-[#F0F0F0]" />

            {/* Eligibility */}
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Eligibility</h2>
              <p className="text-[#555] mb-3">Items must be:</p>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Unused and in original condition
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  In original packaging
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Not damaged by customer misuse
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            {/* Return Shipping */}
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Return Shipping</h2>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-[#1A1A1C] flex-shrink-0">Defective or damaged items:</span>
                  We cover return shipping
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-semibold text-[#1A1A1C] flex-shrink-0">Changed mind/wrong item:</span>
                  Customer pays return shipping cost
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            {/* How to Return */}
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">How to Return</h2>
              <ol className="space-y-4 text-[#555]">
                <li className="flex gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-semibold text-[#1A1A1C] flex-shrink-0">1</span>
                  <span className="pt-1">Contact us at <a href="mailto:nestflowstore@gmail.com" className="text-[#1A1A1C] font-semibold hover:underline">nestflowstore@gmail.com</a> with your order number and reason for return</span>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-semibold text-[#1A1A1C] flex-shrink-0">2</span>
                  <span className="pt-1">We'll provide you with a return shipping label (if applicable)</span>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-semibold text-[#1A1A1C] flex-shrink-0">3</span>
                  <span className="pt-1">Ship the item back to us</span>
                </li>
                <li className="flex gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-semibold text-[#1A1A1C] flex-shrink-0">4</span>
                  <span className="pt-1">Once received and inspected, we'll process your refund within 7 business days</span>
                </li>
              </ol>
            </section>

            <hr className="border-[#F0F0F0]" />

            {/* Refund Timeline */}
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Refund Timeline</h2>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Refund issued to original payment method
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Please allow 5-7 business days for the refund to appear in your account (varies by bank)
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            {/* Non-Returnable */}
            <section>
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-3">Non-Returnable Items</h2>
              <ul className="space-y-2 text-[#555]">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Customized or personalized items
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Items purchased during final sale events
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A1A1C] mt-2 flex-shrink-0" />
                  Severely damaged items (not our fault)
                </li>
              </ul>
            </section>

            <hr className="border-[#F0F0F0]" />

            {/* Questions */}
            <section className="bg-[#FAFAFA] rounded-xl p-8 text-center">
              <h2 className="text-xl font-bold text-[#1A1A1C] font-['Manrope'] mb-2">Questions?</h2>
              <p className="text-[#555]">
                Contact us at{' '}
                <a href="mailto:nestflowstore@gmail.com" className="text-[#1A1A1C] font-semibold hover:underline">
                  nestflowstore@gmail.com
                </a>{' '}
                for any return inquiries.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ReturnsPage;
